import React from "react";
import { useNavigate } from "react-router-dom";
import CheckinLayout from "./CheckinLayout";
import AppButton from "./AppButton";
import { Alert } from "../../components/shared/Alert";
import { useAppointmentBasePath, useAppointmentStepPath } from "../routes/stepConfig";
import { DEMOGRAPHIC_PAGE_ORDER, useWorkflow } from "../contexts/WorkflowContext";

/**
 * Shown when the patient returns to (or reloads) a demographics sub-page after demographics has
 * already been submitted and synced to Athena. Demographics is locked at that point — a re-edit
 * would save locally but never re-sync — so we present the same "already completed" treatment the
 * Screeners / Medical History steps use (shared Alert + Continue), instead of the generic
 * "not available / try again" error the old 404 produced.
 */
const DemographicsAlreadySubmitted: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useAppointmentBasePath();
  const { getNextStepInSequence, isAllStepsCompleted } = useWorkflow();

  // Seed the sequence lookup from the LAST demographic sub-page so getNextStepInSequence falls
  // through to the next workflow type (health history / screener / consent) rather than the next
  // demographic sub-page — which would just be gated as "already submitted" again. Derived from
  // DEMOGRAPHIC_PAGE_ORDER so it stays correct if the sub-page order changes.
  const lastDemographicKey = DEMOGRAPHIC_PAGE_ORDER[DEMOGRAPHIC_PAGE_ORDER.length - 1];
  const lastDemographicPath = useAppointmentStepPath(lastDemographicKey);
  const completionPath = `${basePath}/complete`;

  // Route via the workflow sequence (same as MedicalHistory's already-submitted handler) instead of
  // hard-navigating to medical-history, which doesn't exist for every workflow.
  const handleContinue = () => {
    if (isAllStepsCompleted()) {
      navigate(completionPath);
      return;
    }
    const nextStep = getNextStepInSequence(lastDemographicPath);
    navigate(nextStep?.stepUrl ?? completionPath);
  };

  return (
    <CheckinLayout title="Demographics" subtitle="Your personal and insurance information">
      <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] mt-4">
        <Alert type="info">
          Your demographics have already been submitted and synced to your chart. You can continue with
          the rest of your check-in.
        </Alert>
        <div className="flex justify-center mt-8">
          <AppButton onClick={handleContinue}>Continue check-in</AppButton>
        </div>
      </div>
    </CheckinLayout>
  );
};

export default DemographicsAlreadySubmitted;
